import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { useState, type ReactNode } from 'react';
import superjson from 'superjson';
import type { AppRouter } from '@barev/api';

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();
export type { AppRouter };

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (count, err) => {
          const code = (err as { data?: { httpStatus?: number } }).data?.httpStatus ?? 0;
          return code >= 500 && count < 2;
        },
      },
    },
  });
}

/**
 * Провайдер tRPC + React Query. `auth` — заголовки для каждого запроса
 * (initData Mini App) или credentials для cookie-сессии админки.
 */
export function ApiProvider({ url = '/trpc', headers, withCredentials, children }: { url?: string; headers?: () => Record<string, string>; withCredentials?: boolean; children: ReactNode }) {
  const [queryClient] = useState(makeQueryClient);
  const [client] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url,
          transformer: superjson,
          headers: () => headers?.() ?? {},
          fetch: withCredentials ? (input, init) => fetch(input, { ...init, credentials: 'include' }) : undefined,
        }),
      ],
    }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={client} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
